import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AssistantRuntime } from '@assistant-ui/react';
import {
  AssistantRuntimeProvider,
  useThreadViewportStore,
  ThreadPrimitive,
} from '@assistant-ui/react';
import { Thread } from '@assistant-ui/react-ui';
import { AnchoredViewport } from './anchored-viewport';
import { EmptyStateBrand } from '@/components/operator/EmptyStateBrand';
import { ArrowDownIcon } from 'lucide-react';
import '@assistant-ui/react-ui/styles/index.css';
import '@assistant-ui/react-ui/styles/markdown.css';
import { AgentAssistantMessage } from './agent-assistant-message';
import { AgentUserMessage } from './agent-user-message';
import ToolUI, { SubAgentSpawnToolUI, SubAgentRunToolUI } from './tool-ui';
import { ContextUtilizationBar } from './context-utilization-bar';
import { MentionAwareComposer } from './mention-aware-composer';
import { ComposerAttachmentsProvider } from './composer-attachments-context';
import { PendingAskUserProvider, ComposerAskUserPanel } from './pending-ask-user';
import { CanvasContext } from '../../contexts/CanvasContext';
import { ErrorBoundary } from '../ErrorBoundary';
import { Upload } from 'lucide-react';
import { useComposerAttachments } from './composer-attachments-context';
import { readDroppedEntries, MAX_DROPPED_FILES } from '@/lib/dropped-entries';
import { useToast } from '@/components/ui/Toast';

interface ConversationThreadProps {
  runtime: AssistantRuntime;
  /** Agent ID — used to resolve tool display names from the agent's embedded tools array. */
  agentId?: string;
  /** Bridge ref from `useAgentRuntime`. When provided, the composer
   *  attachments provider registers its `consume` here so the adapter can
   *  await pending uploads before sending. Optional — surfaces without
   *  attachment support can omit it. */
  consumeAttachmentsRef?: React.MutableRefObject<
    (() => Promise<import('../../types').FileRef[]>) | undefined
  >;
}

/**
 * Custom scroll-to-bottom button with a generous threshold (20px) to avoid
 * showing the arrow due to sub-pixel rounding or tiny layout shifts after
 * a run completes. The library's built-in threshold is 1px which is too tight.
 */
const SCROLL_THRESHOLD = 400;

function ScrollToBottom() {
  const viewportStore = useThreadViewportStore();
  const [visible, setVisible] = useState(false);

  // Single source of truth: our own scroll listener with a generous threshold.
  // The library's isAtBottom (1px) is too tight and conflicts, so we ignore it.
  useEffect(() => {
    const el = document.querySelector('.aui-thread-viewport') as HTMLElement | null;
    if (!el) return;
    const check = () => {
      const gap = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight);
      setVisible(gap >= SCROLL_THRESHOLD);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const obs = new ResizeObserver(check);
    obs.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      obs.disconnect();
    };
  }, []);

  const handleClick = useCallback(() => {
    viewportStore.getState().scrollToBottom({ behavior: 'auto' });
  }, [viewportStore]);

  if (!visible) return null;

  return (
    <button
      onClick={handleClick}
      className="aui-thread-scroll-to-bottom"
      aria-label="Scroll to bottom"
    >
      <ArrowDownIcon />
    </button>
  );
}

export function ConversationThread({ runtime, agentId, consumeAttachmentsRef }: ConversationThreadProps) {
  // Conversation id flows in via CanvasProvider (both sandbox and operator
  // mount one). It's the VFS owner for composer uploads.
  const canvasCtx = useContext(CanvasContext);
  const conversationId = canvasCtx?.conversationId || undefined;

  return (
    // Scoped boundary: a render error inside the assistant-ui runtime (e.g.
    // the tapResources duplicate-toolCallId throw) must take down only the
    // chat panel, not the whole app — the nearest other boundary is at the
    // App root and swaps out every route.
    <ErrorBoundary fallback={(reset) => <ThreadCrashFallback onRetry={reset} />}>
    <AssistantRuntimeProvider runtime={runtime}>
      <ComposerAttachmentsProvider ownerId={conversationId}>
       <PendingAskUserProvider>
        <AttachmentBridge bridgeRef={consumeAttachmentsRef} />
        <SubAgentSpawnToolUI />
        <SubAgentRunToolUI />
        <ToolUI />
        <ContextUtilizationBar />
        <ChatPanelDrop>
          {(handlers) => (
            <Thread.Root {...handlers}>
              <AnchoredViewport>
                <ThreadPrimitive.Empty>
                  <EmptyStateBrand title="How can I help you today?" />
                </ThreadPrimitive.Empty>
                <Thread.Messages components={{ UserMessage: AgentUserMessage, AssistantMessage: AgentAssistantMessage }} />
                <Thread.FollowupSuggestions />
              </AnchoredViewport>
              {/* Composer lives OUTSIDE the scroll viewport so it is fixed at
                  the bottom of the panel and never moves with scroll. The
                  viewport is flex:1 (see AnchoredViewport) so this footer keeps
                  its natural height beneath it. */}
              <Thread.ViewportFooter>
                <ScrollToBottom />
                <ComposerAskUserPanel />
                <MentionAwareComposer agentId={agentId} />
              </Thread.ViewportFooter>
            </Thread.Root>
          )}
        </ChatPanelDrop>
       </PendingAskUserProvider>
      </ComposerAttachmentsProvider>
    </AssistantRuntimeProvider>
    </ErrorBoundary>
  );
}

/**
 * Panel-scoped crash fallback for the chat thread. Fills the panel (the
 * thread's flex column) instead of the full screen so the surrounding app
 * chrome stays visible and interactive.
 */
function ThreadCrashFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <p className="text-sm font-medium text-foreground">The chat failed to render</p>
        <p className="text-sm text-muted-foreground">
          Your conversation is saved on the server — nothing is lost.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="focus-ring rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/** Registers the composer attachments provider's `consume` on the runtime
 *  hook's bridge ref so the adapter can await uploads at invoke time. */
function AttachmentBridge({
  bridgeRef,
}: {
  bridgeRef?: React.MutableRefObject<
    (() => Promise<import('../../types').FileRef[]>) | undefined
  >;
}) {
  const ctx = useComposerAttachments();
  useEffect(() => {
    if (!bridgeRef) return;
    bridgeRef.current = ctx?.consume;
    return () => { bridgeRef.current = undefined; };
  }, [bridgeRef, ctx?.consume]);
  return null;
}

/**
 * Produces drag-and-drop event handlers + a portaled overlay for the
 * composer attachments flow. The handlers attach DIRECTLY to Thread.Root
 * (no wrapping div) via a render-prop API, so the layout chain is
 * identical to a vanilla Thread.Root render and there's nothing new to
 * push the chat panel past its bounds. The overlay is rendered via a
 * React portal to `document.body` and positioned with `position: fixed`
 * over Thread.Root's bounding rect while a drag is in progress.
 */
type DragHandlerProps = {
  onDragEnter?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
};

function ChatPanelDrop({ children }: { children: (handlers: DragHandlerProps) => React.ReactNode }) {
  const attachmentsCtx = useComposerAttachments();
  const toast = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null);

  if (!attachmentsCtx) return <>{children({})}</>;

  const handlers: DragHandlerProps = {
    onDragEnter: (e) => {
      e.preventDefault();
      dragDepth.current += 1;
      if (!isDragging) {
        setOverlayRect((e.currentTarget as HTMLElement).getBoundingClientRect());
        setIsDragging(true);
      }
    },
    onDragOver: (e) => { e.preventDefault(); },
    onDragLeave: (e) => {
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    },
    onDrop: async (e) => {
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      // The DataTransfer's items list is invalidated once the event
      // returns, so we must walk it synchronously. readDroppedEntries
      // captures the entries up-front before awaiting any async file IO.
      const result = await readDroppedEntries(e.dataTransfer);
      if (result.kind === 'too-many') {
        toast.error(
          `You can attach up to ${MAX_DROPPED_FILES} files per message (you dropped ${result.count}).`,
        );
        return;
      }
      // Folders are flattened into the attachment list — composer
      // attachments are a flat row, and the original folder structure
      // doesn't survive the upload anyway (each file lands under the
      // composer's own `prefix`).
      if (result.entries.length === 0) return;
      attachmentsCtx.addFiles(result.entries.map((entry) => entry.file));
    },
  };

  return (
    <>
      {children(handlers)}
      {isDragging && overlayRect && createPortal(
        <div
          className="pointer-events-none fixed z-40 flex items-center justify-center bg-background/40 backdrop-blur-[1px]"
          style={{
            top: overlayRect.top,
            left: overlayRect.left,
            width: overlayRect.width,
            height: overlayRect.height,
          }}
        >
          <div className="flex max-w-md flex-col items-center gap-6 rounded-3xl border border-foreground/40 bg-card/70 px-12 py-10 text-center shadow-2xl shadow-black/70 ring-1 ring-black/20 backdrop-blur-md">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary shadow-lg shadow-primary/30">
              <Upload className="h-8 w-8 text-white" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-base font-semibold text-foreground">Drop your files here</p>
              <p className="text-sm text-muted-foreground">to add them to the conversation</p>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
